import { useCallback, useEffect, useState } from 'react'

import { cn } from '@/utils/cn'

interface FyersStatus {
  configured: boolean
  connected: boolean
  app_id?: string
  auth_url?: string | null
  obtained_at?: number | null
}

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const [fyers, setFyers] = useState<FyersStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [authCodeInput, setAuthCodeInput] = useState('')
  const [authCodeError, setAuthCodeError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const API = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8000'

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/v1/fyers/status`)
      setFyers((await res.json()) as FyersStatus)
    } catch {
      setFyers({ configured: false, connected: false })
    } finally {
      setLoading(false)
    }
  }, [API])

  useEffect(() => {
    void fetchStatus()
  }, [fetchStatus])

  function openAuthWindow() {
    if (!fyers?.auth_url) return
    window.open(fyers.auth_url, '_blank', 'width=600,height=700,noopener')
    // Poll for connection every 3s after opening
    const interval = setInterval(() => {
      void fetch(`${API}/api/v1/fyers/status`)
        .then((r) => r.json())
        .then((s: FyersStatus) => {
          if (s.connected) {
            setFyers(s)
            clearInterval(interval)
          }
        })
    }, 3000)
    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(interval), 5 * 60 * 1000)
  }

  async function submitAuthCode() {
    const code = authCodeInput.trim()
    if (!code) return
    setSubmitting(true)
    setAuthCodeError('')
    try {
      const res = await fetch(`${API}/api/v1/fyers/auth-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_code: code }),
      })
      const data = (await res.json()) as { success: boolean; error?: string }
      if (data.success) {
        setAuthCodeInput('')
        await fetchStatus()
      } else {
        setAuthCodeError(data.error ?? 'Failed — check backend logs')
      }
    } catch {
      setAuthCodeError('Network error')
    } finally {
      setSubmitting(false)
    }
  }

  async function disconnect() {
    await fetch(`${API}/api/v1/fyers/disconnect`, { method: 'DELETE' })
    await fetchStatus()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex h-auto max-h-[90vh] w-[95vw] max-w-[560px] flex-col overflow-hidden rounded-lg border border-tv-border bg-tv-bg-secondary shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-tv-border px-4 py-3">
          <h2 className="text-sm font-semibold text-tv-text-primary">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-tv-text-muted hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Data Sources section */}
          <div className="mb-2">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-tv-text-muted">
              Data Sources — India (NSE)
            </h3>

            {loading ? (
              <p className="text-xs text-tv-text-muted">Loading…</p>
            ) : (
              <div className="rounded-lg border border-tv-border bg-tv-bg-primary p-4">
                {/* Fyers row */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-tv-text-primary">Fyers API v3</span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                          fyers?.connected
                            ? 'bg-tv-green/20 text-tv-green'
                            : fyers?.configured
                              ? 'bg-tv-orange/20 text-tv-orange'
                              : 'bg-tv-bg-tertiary text-tv-text-muted'
                        )}
                      >
                        {fyers?.connected
                          ? '● Connected'
                          : fyers?.configured
                            ? '○ Not authenticated'
                            : 'Not configured'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-tv-text-muted">
                      {fyers?.connected
                        ? `App: ${fyers.app_id} · Token obtained ${fyers.obtained_at ? new Date(fyers.obtained_at * 1000).toLocaleDateString() : 'recently'}`
                        : 'When connected, Fyers is used instead of Yahoo Finance for all NSE stocks and indices.'}
                    </p>
                  </div>

                  {fyers?.connected ? (
                    <button
                      type="button"
                      onClick={() => void disconnect()}
                      className="shrink-0 rounded border border-tv-border px-3 py-1.5 text-xs text-tv-text-muted hover:border-tv-red/50 hover:text-tv-red"
                    >
                      Disconnect
                    </button>
                  ) : fyers?.configured ? (
                    <button
                      type="button"
                      onClick={openAuthWindow}
                      className="shrink-0 rounded bg-tv-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-tv-blue-hover"
                    >
                      Connect Fyers
                    </button>
                  ) : null}
                </div>

                {/* Auth flow when configured but not connected */}
                {fyers?.configured && !fyers.connected && (
                  <div className="mt-4 space-y-3">
                    <div className="rounded border border-tv-border-light bg-tv-bg-secondary p-3 text-[11px] text-tv-text-secondary">
                      <p className="font-medium text-tv-text-primary">How to connect:</p>
                      <ol className="mt-1.5 list-inside list-decimal space-y-1">
                        <li>
                          Click <strong>Connect Fyers</strong> — a Fyers login window opens
                        </li>
                        <li>Log in with your Fyers account</li>
                        <li>
                          After login, the window closes automatically and MarketHub Pro reconnects
                        </li>
                      </ol>
                      <p className="mt-2 text-tv-text-muted">
                        If the window doesn&apos;t close, copy the{' '}
                        <code className="rounded bg-tv-bg-tertiary px-1">auth_code=…</code> value
                        from the browser URL bar and paste it below.
                      </p>
                    </div>

                    {/* Manual code input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Paste auth_code here (manual fallback)"
                        value={authCodeInput}
                        onChange={(e) => setAuthCodeInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && void submitAuthCode()}
                        className="flex-1 rounded border border-tv-border bg-tv-bg-tertiary px-3 py-1.5 text-xs text-tv-text-primary placeholder-tv-text-muted outline-none focus:border-tv-blue"
                      />
                      <button
                        type="button"
                        onClick={() => void submitAuthCode()}
                        disabled={submitting || !authCodeInput.trim()}
                        className="shrink-0 rounded bg-tv-bg-tertiary px-3 py-1.5 text-xs text-tv-text-secondary hover:bg-tv-border hover:text-tv-text-primary disabled:opacity-40"
                      >
                        {submitting ? '…' : 'Submit'}
                      </button>
                    </div>
                    {authCodeError && <p className="text-[11px] text-tv-red">{authCodeError}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Yahoo Finance fallback note */}
            <div className="mt-3 flex items-center gap-2 rounded border border-tv-border bg-tv-bg-primary p-3">
              <div className="flex-1">
                <span className="text-sm font-medium text-tv-text-primary">Yahoo Finance</span>
                <span className="ml-2 rounded-full bg-tv-bg-tertiary px-2 py-0.5 text-[10px] text-tv-text-muted">
                  {fyers?.connected ? 'Fallback' : '● Active'}
                </span>
                <p className="mt-0.5 text-[11px] text-tv-text-muted">
                  {fyers?.connected
                    ? 'Used as fallback for US stocks and symbols not available on Fyers.'
                    : 'Default source for NSE stocks, indices, and US stocks. Free, no auth required.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
